import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@quebon/ui/components/dialog'
import { useAuthStore } from '@/store/authStore'
import { authApi } from '@/api/auth'
import { phoneApi } from '@/api/phone'
import { toast } from 'sonner'
import { getApiErrorMessage, getApiErrorCode } from '@/utils/apiError'
import {
  User,
  Mail,
  Briefcase,
  Building,
  Phone,
  Shield,
  Edit,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Loader2,
} from 'lucide-react'

// ============================================================================
// Dummy Data (API 연결 전까지 사용)
// ============================================================================

const dummyProfileData = {
  employeeNumber: 'EMP-2024-001234',
  department: '디지털혁신본부',
  position: '책임',
  mobile: '010-1234-5678',
  lastPasswordChange: '2024-10-15',
}

// ============================================================================
// Types
// ============================================================================

interface InfoItemProps {
  icon: React.ReactNode
  label: string
  value: string
  badge?: string
}

// ============================================================================
// Sub Components
// ============================================================================

function InfoItem({ icon, label, value, badge }: InfoItemProps) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-gray-400 mt-1">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm text-gray-600">{label}</p>
          {badge && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-medium">
              {badge}
            </span>
          )}
        </div>
        <p className="font-medium text-gray-900 truncate">{value || '-'}</p>
      </div>
    </div>
  )
}

// ============================================================================
// Modals
// ============================================================================

const DEFAULT_COUNTRY_CODE = '+82'
const PHONE_PATTERN = /^01[016789]\d{7,8}$/

type VerificationStep = 'input' | 'verify' | 'complete'

interface PhoneChangeModalProps {
  isOpen: boolean
  onClose: () => void
  currentPhone: string | null
  currentCountryCode: string | null
  onPhoneChanged: () => void
}

function PhoneChangeModal({ isOpen, onClose, currentPhone, currentCountryCode, onPhoneChanged }: PhoneChangeModalProps) {
  const { t } = useTranslation('learner')
  const [step, setStep] = useState<VerificationStep>('input')
  const countryCode = DEFAULT_COUNTRY_CODE
  const [phoneNumber, setPhoneNumber] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationToken, setVerificationToken] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [timer, setTimer] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const resetForm = useCallback(() => {
    setStep('input')
    setPhoneNumber('')
    setVerificationCode('')
    setVerificationToken('')
    setIsLoading(false)
    setError('')
    setTimer(0)
    if (timerRef.current) clearInterval(timerRef.current)
  }, [])

  useEffect(() => {
    if (isOpen) resetForm()
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [isOpen, resetForm])

  const startTimer = (seconds: number) => {
    setTimer(seconds)
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const validatePhone = () => {
    if (!phoneNumber.trim()) {
      setError(t('profile.phone.enterPhone'))
      return false
    }
    if (!PHONE_PATTERN.test(phoneNumber)) {
      setError(t('profile.phone.invalidFormat'))
      return false
    }
    return true
  }

  const handleSendCode = async () => {
    if (!validatePhone()) return
    setIsLoading(true)
    setError('')
    try {
      const res = await phoneApi.sendCode({ countryCode, phoneNumber })
      startTimer(res.data.data.expiresInSeconds)
      setStep('verify')
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('profile.phone.smsFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      setError(t('profile.phone.enterCode'))
      return
    }
    setIsLoading(true)
    setError('')
    try {
      const res = await phoneApi.verifyCode({ countryCode, phoneNumber, code: verificationCode })
      setVerificationToken(res.data.data.verificationToken)
      // 바로 전화번호 변경 실행
      await phoneApi.updatePhone({ countryCode, phoneNumber, verificationToken: res.data.data.verificationToken })
      setStep('complete')
      onPhoneChanged()
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('profile.phone.verifyFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setIsLoading(true)
    setError('')
    try {
      const res = await phoneApi.sendCode({ countryCode, phoneNumber })
      startTimer(res.data.data.expiresInSeconds)
      setVerificationCode('')
      setError('')
    } catch (err: any) {
      setError(err.response?.data?.error?.message || t('profile.phone.resendFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const maskPhone = (phone: string | null) => {
    if (!phone || phone.length < 4) return phone || '-'
    return phone.slice(0, 3) + '-****-' + phone.slice(-4)
  }

  const phoneTitle = (
    <div>
      <div>{t('profile.phone.title')}</div>
      {currentPhone && (
        <p className="text-sm text-gray-500 mt-1 font-normal">{t('profile.phone.current')} {currentCountryCode} {maskPhone(currentPhone)}</p>
      )}
    </div>
  )

  const phoneFooter = (
    <div className="flex justify-end gap-3">
      {step === 'input' && (
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            {t('profile.cancel')}
          </button>
          <button
            onClick={handleSendCode}
            disabled={isLoading || !phoneNumber}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('profile.phone.sendCode')}
          </button>
        </>
      )}
      {step === 'verify' && (
        <>
          <button onClick={() => { setStep('input'); setError('') }} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            {t('profile.phone.back')}
          </button>
          <button
            onClick={handleVerifyCode}
            disabled={isLoading || verificationCode.length !== 6}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('profile.phone.verify')}
          </button>
        </>
      )}
      {step === 'complete' && (
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors">
          {t('profile.phone.confirm')}
        </button>
      )}
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{phoneTitle}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {step === 'input' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.phone.newPhone')}</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => { setPhoneNumber(e.target.value.replace(/[^0-9]/g, '')); setError('') }}
              placeholder={t('profile.phone.phonePlaceholder')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${error ? 'border-red-500' : 'border-gray-300'}`}
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-1">{t('profile.phone.phoneHint')}</p>
          </div>
        )}

        {step === 'verify' && (
          <>
            <p className="text-sm text-gray-600">
              <span className="font-medium">{countryCode} {phoneNumber}</span>{t('profile.phone.codeSent')}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.phone.codeLabel')}</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => { setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6)); setError('') }}
                  placeholder="000000"
                  maxLength={6}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors text-center text-lg tracking-widest ${error ? 'border-red-500' : 'border-gray-300'}`}
                  autoFocus
                />
                {timer > 0 && (
                  <span className="flex items-center text-sm font-mono text-orange-600 min-w-[48px]">
                    {formatTimer(timer)}
                  </span>
                )}
              </div>
            </div>
            {timer === 0 && (
              <button
                onClick={handleResendCode}
                disabled={isLoading}
                className="text-sm text-primary-600 hover:text-primary-700 underline"
              >
                {t('profile.phone.resendCode')}
              </button>
            )}
          </>
        )}

        {step === 'complete' && (
          <div className="text-center py-4">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-gray-900">{t('profile.phone.changed')}</p>
            <p className="text-sm text-gray-500 mt-1">{countryCode} {phoneNumber}</p>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            {error}
          </p>
        )}
      </div>
      <DialogFooter>{phoneFooter}</DialogFooter>
    </DialogContent>
    </Dialog>
  )
}

interface PasswordChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onPasswordChanged: () => void
}

function PasswordChangeModal({ isOpen, onClose, onPasswordChanged }: PasswordChangeModalProps) {
  const { t } = useTranslation('learner')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ current?: string; new?: string; confirm?: string; general?: string }>({})

  // Password validation (AccountSetupPage와 동일한 규칙)
  const hasMinLength = newPassword.length >= 8
  const hasLetter = /[a-zA-Z]/.test(newPassword)
  const hasNumber = /[0-9]/.test(newPassword)
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword)
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0

  const resetForm = useCallback(() => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setShowCurrentPassword(false)
    setShowNewPassword(false)
    setShowConfirmPassword(false)
    setIsLoading(false)
    setErrors({})
  }, [])

  useEffect(() => {
    if (isOpen) {
      resetForm()
    }
  }, [isOpen, resetForm])

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const validate = (): boolean => {
    const newErrors: typeof errors = {}

    if (!currentPassword) {
      newErrors.current = t('profile.enterCurrentPassword')
    }

    if (!newPassword) {
      newErrors.new = t('profile.enterNewPassword')
    } else if (!hasMinLength) {
      newErrors.new = t('profile.passwordMinLength')
    } else if (!hasLetter || !hasNumber) {
      newErrors.new = t('profile.passwordLetterNumber')
    }

    if (!confirmPassword) {
      newErrors.confirm = t('profile.reenterNewPassword')
    } else if (newPassword !== confirmPassword) {
      newErrors.confirm = t('profile.passwordMismatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return

    setIsLoading(true)
    setErrors({})

    try {
      await authApi.changePassword(currentPassword, newPassword)
      onPasswordChanged()
      toast.success(t('profile.passwordChanged'))
      handleClose()
    } catch (error: any) {
      const code = getApiErrorCode(error)
      const message = getApiErrorMessage(error, t)
      if (code === 'CURRENT_PASSWORD_MISMATCH') {
        setErrors({ current: message })
      } else if (code === 'NEW_PASSWORD_SAME_AS_CURRENT') {
        setErrors({ new: message })
      } else {
        setErrors({ general: message })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const passwordTitle = (
    <div>
      <div>{t('profile.changePassword')}</div>
      <p className="text-sm text-gray-500 mt-1 font-normal">{t('profile.changePasswordDesc')}</p>
    </div>
  )

  const passwordFooter = (
    <div className="flex justify-end gap-3">
      <button
        onClick={handleClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        {t('profile.cancel')}
      </button>
      <button
        onClick={handleSave}
        disabled={isLoading}
        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {t('profile.changePassword')}
      </button>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{passwordTitle}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        {/* Current Password */}
        <div className="space-y-1">
          <label htmlFor="current-password" className="block text-sm font-medium text-gray-700">
            {t('profile.currentPassword')}
          </label>
          <div className="relative">
            <input
              id="current-password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder={t('profile.currentPasswordPlaceholder')}
              className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${
                errors.current ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            >
              {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.current && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errors.current}
            </p>
          )}
        </div>

        {/* New Password */}
        <div className="space-y-1">
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
            {t('profile.newPassword')}
          </label>
          <div className="relative">
            <input
              id="new-password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('profile.newPasswordPlaceholder')}
              className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${
                errors.new ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.new && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errors.new}
            </p>
          )}

          {/* Password Requirements (AccountSetupPage와 동일) */}
          {newPassword.length > 0 && (
            <div className="mt-3 space-y-1 text-sm">
              <div className={hasMinLength ? 'text-green-600' : 'text-gray-400'}>
                {hasMinLength ? '✓' : '✗'} {t('profile.validation.minLength')}
              </div>
              <div className={hasLetter && hasNumber ? 'text-green-600' : 'text-gray-400'}>
                {hasLetter && hasNumber ? '✓' : '✗'} {t('profile.validation.letterAndNumber')}
              </div>
              <div className={hasSpecial ? 'text-green-600' : 'text-gray-400'}>
                {hasSpecial ? '✓' : '✗'} {t('profile.validation.specialChar')}
              </div>
              {confirmPassword.length > 0 && (
                <div className={passwordsMatch ? 'text-green-600' : 'text-red-500'}>
                  {passwordsMatch ? '✓' : '✗'} {t('profile.validation.match')}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div className="space-y-1">
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
            {t('profile.confirmPassword')}
          </label>
          <div className="relative">
            <input
              id="confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('profile.confirmPasswordPlaceholder')}
              className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors ${
                errors.confirm ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.confirm && (
            <p className="text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errors.confirm}
            </p>
          )}
        </div>
        {errors.general && (
          <p className="text-sm text-red-500 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {errors.general}
          </p>
        )}
      </div>
      <DialogFooter>{passwordFooter}</DialogFooter>
    </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Main Page Component
// ============================================================================

export default function MyProfile() {
  const { t } = useTranslation('learner')
  const { user, checkAuth } = useAuthStore()
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)

  const initials = user?.fullName?.slice(0, 2) || user?.email?.slice(0, 2) || '??'

  const handlePasswordChanged = async () => {
    // Refresh user data to get updated passwordChangedAt
    await checkAuth()
  }

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return t('profile.none')
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return t('profile.none')
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <User className="w-8 h-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t('profile.title')}</h1>
        </div>
        <p className="text-gray-500">{t('profile.subtitle')}</p>
      </div>

      {/* Cards */}
      <div className="space-y-6">
        {/* Profile Info Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('profile.basicInfo')}</h2>
          </div>

          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="h-24 w-24 rounded-full bg-primary-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
              {initials}
            </div>

            {/* Info Grid */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoItem
                icon={<User className="h-4 w-4" />}
                label={t('profile.name')}
                value={user?.fullName || ''}
              />
              <InfoItem
                icon={<Mail className="h-4 w-4" />}
                label={t('profile.email')}
                value={user?.email || ''}
                badge={t('profile.readOnly')}
              />
              <InfoItem
                icon={<Briefcase className="h-4 w-4" />}
                label={t('profile.employeeId')}
                value={dummyProfileData.employeeNumber}
                badge={t('profile.readOnly')}
              />
              <InfoItem
                icon={<Building className="h-4 w-4" />}
                label={t('profile.department')}
                value={dummyProfileData.department}
              />
              <InfoItem
                icon={<User className="h-4 w-4" />}
                label={t('profile.position')}
                value={dummyProfileData.position}
              />
            </div>
          </div>
        </div>

        {/* Security Card */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">{t('profile.security')}</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 mb-1">{t('profile.password')}</p>
                <p className="text-sm text-gray-600">
                  {t('profile.lastChanged', { date: formatDate(user?.passwordChangedAt) })}
                </p>
              </div>
              <button
                onClick={() => setIsPasswordModalOpen(true)}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Lock className="h-4 w-4" />
                {t('profile.change')}
              </button>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                {t('profile.passwordRecommendation')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onPasswordChanged={handlePasswordChanged}
      />
    </div>
  )
}
